import React from 'react';
import {
  HStack, Button, ButtonIcon, ButtonText, Badge, BadgeText, Box, Icon, Pressable,
} from '@gluestack-ui/themed';
import { Sparkles, RefreshCw } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import SubmitButton from './SubmitButton';

export interface EditorToolbarProps {
  disableButtons?: boolean;
  onEvaluate?: () => void | Promise<void>;
  onSubmit?: () => void | Promise<void>;
  canSubmit?: boolean;
  showEvaluate?: boolean;
  showSubmitButton?: boolean;
  showWordcountInline?: boolean;
  wordcount?: { bodyWords: number; allWords?: number };
  targetWordcount?: number;
  wordcountColor?: string;
  compactSaveLabel?: string;
  compactSaveColor?: string;
  onRetry?: () => void;
}

const EditorToolbarComponent: React.FC<EditorToolbarProps> = ({
  disableButtons,
  onEvaluate,
  canSubmit,
  onSubmit,
  showEvaluate,
  showSubmitButton,
  showWordcountInline,
  wordcount,
  targetWordcount,
  wordcountColor,
  compactSaveLabel,
  compactSaveColor,
  onRetry,
}) => {
  const { t } = useTranslation();
  return (
    <HStack
      testID="editor-toolbar"
      space="sm"
      justifyContent="flex-end"
      alignItems="center"
      position="absolute"
      top={2}
      right={2}
      zIndex={5}
      px="$2"
      h={40}
    >
      <HStack space="sm" alignItems="center">
        {showWordcountInline ? (
          <HStack alignItems="center" space="sm" mr="$2">
            {compactSaveLabel ? (
              <Pressable onPress={compactSaveLabel === 'Error' ? onRetry : undefined}>
                <Badge size="sm" action="muted">
                  {compactSaveLabel === 'Error' && (
                    <Icon as={RefreshCw} size="2xs" color={compactSaveColor} mr="$1" />
                  )}
                  <BadgeText size="xs" color={compactSaveColor}>
                    {compactSaveLabel}
                  </BadgeText>
                </Badge>
              </Pressable>
            ) : null}
            {(targetWordcount ?? 0) > 0 ? (
              <Badge size="sm" action="muted">
                <BadgeText size="xs" color={wordcountColor}>
                  {`${wordcount?.bodyWords ?? 0}/${targetWordcount ?? 0}`}
                </BadgeText>
              </Badge>
            ) : null}
          </HStack>
        ) : null}
        <Box w={1} h={24} bg="$borderLight" mr="$2" />
        {showEvaluate ? (
          <Button
            testID="evaluate-button"
            isDisabled={Boolean(disableButtons)}
            size="xs"
            h={25}
            action="primary"
            variant="solid"
            onPress={onEvaluate}
          >
            <ButtonIcon as={Sparkles} size="sm" $base-mr="$0" $sm-mr="$2" color="$textInverted" />
            <ButtonText $base-display="none" $sm-display="flex" color="$textInverted">
              Evaluate
            </ButtonText>
          </Button>
        ) : null}
        <SubmitButton
          show={Boolean(showSubmitButton)}
          isDisabled={Boolean(disableButtons) || !canSubmit}
          onSubmit={onSubmit}
          label={t('student.task.composer.buttons.mark-as-done')}
        />
      </HStack>
    </HStack>
  );
};

const EditorToolbar = React.memo(EditorToolbarComponent);

export default EditorToolbar;
